/**
 * StarEater.ts
 * Pooled hostile scout for the World 2 space shmup.
 *
 * Behaviour: 1 HP scout that runs a four-phase predator sequence
 * triggered by elapsed-time-since-spawn:
 *
 *     DRIFT_PRE (vx = −DRIFT_SPEED) ──── pre-windup ────▶ TELEGRAPH
 *     TELEGRAPH (vx = vy = 0)       ──── stationary ───▶ SCAN
 *     SCAN      (vx = vy = 0)       ──── locks ship Y ─▶ DASH
 *     DASH      (vx = −DASH_SPEED,
 *                vy toward locked Y) ─── one-shot ─────▶ DRIFT_POST
 *     DRIFT_POST (vx = −DRIFT_SPEED, vy = 0) ── culled by left-margin.
 *
 * Thematically the Star-Eater scouts foreshadow the World 5 boss — small,
 * dark, predatory silhouettes that read as a different *kind* of threat
 * from the strafing fighter / heavy bomber / orbital satellite trio. The
 * three-phase windup gives the player ~1 s to shoot the 1 HP scout before
 * the dash commits; the dash itself is on a deterministic line so a
 * vertical reposition during the SCAN window is a clean dodge.
 *
 * Time-based phase transitions (not camera-X based) keep the windup
 * deterministic from the moment of acquire — pure local state, trivially
 * testable, no dependency on camera scroll rate.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift (Level 2-4 scout fleet).
 *
 * @module StarEater
 */

import { SpaceEnemyBase } from './SpaceEnemyBase';
import { spaceState } from '../engine/spaceState';
import Logger from '../utils/logger';
import type { LevelRuntime } from '../engine/gameState';
import {
  STAR_EATER_WIDTH,
  STAR_EATER_HEIGHT,
  STAR_EATER_HP,
  STAR_EATER_DRIFT_SPEED,
  STAR_EATER_DRIFT_PRE_DURATION_S,
  STAR_EATER_TELEGRAPH_DURATION_S,
  STAR_EATER_SCAN_DURATION_S,
  STAR_EATER_DASH_DURATION_S,
  STAR_EATER_DASH_SPEED,
  STAR_EATER_DASH_VY_MAX,
  SPACE_ENEMY_OFFSCREEN_MARGIN,
  type SpaceEnemyKind,
  type StarEaterAiPhase,
} from '../../constants/spaceEnemyConstants';

/**
 * Fast left-drifting scout with a telegraph + scan + dash predator AI.
 * Pooled — use after `acquire()` from the star-eater pool in
 * `spaceEnemyPools`.
 */
export class StarEater extends SpaceEnemyBase {
  /** Discriminator read by the renderer to pick a colour / sprite. */
  public readonly kind: SpaceEnemyKind = 'starEater';

  /** Current predator-AI phase. Reset to `'DRIFT_PRE'` on every `spawn()`. */
  public aiPhase: StarEaterAiPhase = 'DRIFT_PRE';

  /**
   * Seconds remaining in the current phase. Counts down on every
   * `update()` and drives phase transitions. Reset on `spawn()` /
   * `reset()`.
   */
  public phaseTimer = 0;

  constructor() {
    super(STAR_EATER_WIDTH, STAR_EATER_HEIGHT);
  }

  /**
   * Initialise the scout at a world position. Marks it live, assigns a
   * fresh `spawnId` via the base class, and starts the predator FSM in
   * `DRIFT_PRE`.
   *
   * @param x - Spawn X (world space, top-left of hitbox).
   * @param y - Spawn Y (world space, top-left of hitbox).
   */
  public spawn(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.vx = -STAR_EATER_DRIFT_SPEED;
    this.vy = 0;
    this.hp = STAR_EATER_HP;
    this.aiPhase = 'DRIFT_PRE';
    this.phaseTimer = STAR_EATER_DRIFT_PRE_DURATION_S;
    this.markSpawned();
  }

  /**
   * Reset to a clean pooled state. Extends `SpaceEnemyBase.reset()` so
   * the predator-AI fields don't leak between pool acquisitions.
   */
  public override reset(): void {
    super.reset();
    this.aiPhase = 'DRIFT_PRE';
    this.phaseTimer = 0;
  }

  /**
   * Advance one fixed timestep. Drives the predator FSM, then integrates
   * the current velocity, then runs the standard left-margin cull.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.stepAi(dt);

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // DECISION: Camera-relative left cull on the RIGHT edge of the scout
    // so a scout whose left edge has crossed the camera left edge does
    // not vanish while still partially on-screen. Mirrors FighterDrone /
    // BomberDrone.
    const leftCullX = spaceState.cameraX - SPACE_ENEMY_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }

  /**
   * Advance the predator AI by one fixed timestep. Pure state-machine
   * transitions on `phaseTimer`. Velocity changes are applied here;
   * position integration happens in {@link update}.
   *
   * DECISION: when a phase boundary is crossed, the negative-going
   * residual of `phaseTimer` is carried into the next phase's timer so
   * cumulative elapsed-time-since-spawn stays monotonic. Without this
   * carry, FP drift accumulates a one-frame slip at every boundary and
   * tests pinning "X seconds after spawn we should be in phase Y" fail.
   *
   * The `while` loop also handles unusually large `dt` (e.g. a single
   * frame drop spanning multiple phase windows) — it walks the FSM
   * forward until the remaining timer is positive.
   *
   * @param dt - Fixed timestep in seconds.
   */
  private stepAi(dt: number): void {
    this.phaseTimer -= dt;
    while (this.phaseTimer <= 0 && this.aiPhase !== 'DRIFT_POST') {
      const overshoot = -this.phaseTimer;
      switch (this.aiPhase) {
        case 'DRIFT_PRE': {
          // DRIFT_PRE → TELEGRAPH: stop drifting, hold position.
          this.aiPhase = 'TELEGRAPH';
          Logger.debug('StarEater', 'DRIFT_PRE → TELEGRAPH (drift window elapsed)');
          this.phaseTimer = STAR_EATER_TELEGRAPH_DURATION_S - overshoot;
          this.vx = 0;
          this.vy = 0;
          break;
        }
        case 'TELEGRAPH': {
          // TELEGRAPH → SCAN: still stationary, target lock happens at
          // the SCAN→DASH transition (end of SCAN window) so the player
          // can still reposition during SCAN to invalidate the lock.
          this.aiPhase = 'SCAN';
          Logger.debug('StarEater', 'TELEGRAPH → SCAN (telegraph window elapsed)');
          this.phaseTimer = STAR_EATER_SCAN_DURATION_S - overshoot;
          break;
        }
        case 'SCAN': {
          // SCAN → DASH: snapshot the ship's Y centre and lock the dash
          // trajectory toward it. One-shot: vy is set once and not
          // re-tracked during the dash window.
          this.aiPhase = 'DASH';
          Logger.debug('StarEater', 'SCAN → DASH (target locked)');
          this.phaseTimer = STAR_EATER_DASH_DURATION_S - overshoot;
          this.vx = -STAR_EATER_DASH_SPEED;
          this.vy = this.computeDashVy();
          break;
        }
        case 'DASH': {
          // DASH → DRIFT_POST: committed left-drift until left-margin cull.
          this.aiPhase = 'DRIFT_POST';
          Logger.debug('StarEater', 'DASH → DRIFT_POST (dash window spent)');
          // DECISION: very large timer so DRIFT_POST never re-fires; the
          // left-margin cull in update() is the only exit from this state.
          this.phaseTimer = Number.POSITIVE_INFINITY;
          this.vx = -STAR_EATER_DRIFT_SPEED;
          this.vy = 0;
          break;
        }
      }
    }
  }

  /**
   * Compute the dash vy at the SCAN→DASH transition. Locks onto the
   * ship's Y centre and clamps to ±DASH_VY_MAX so the dash arrives near
   * the locked Y over the DASH_DURATION window without overshooting.
   *
   * Returns 0 when the ship is absent (defensive — `spaceState.ship` is
   * non-null for the entire 2-4 wave, but a paused or torn-down scene
   * could in theory advance the FSM with no ship).
   */
  private computeDashVy(): number {
    const ship = spaceState.ship;
    if (ship === null) return 0;

    const scoutCentreY = this.y + this.height / 2;
    const shipCentreY = ship.y + ship.height / 2;
    const dy = shipCentreY - scoutCentreY;
    if (dy === 0) return 0;

    // DECISION: clamp by magnitude (not Math.sign × MAX) so a short dash
    // distance produces a slower vy that arrives exactly on the locked
    // Y at end-of-dash. Equivalent to "lerp by dt over the dash window"
    // up to the DASH_VY_MAX speed cap.
    const idealVy = dy / STAR_EATER_DASH_DURATION_S;
    if (idealVy > STAR_EATER_DASH_VY_MAX) return STAR_EATER_DASH_VY_MAX;
    if (idealVy < -STAR_EATER_DASH_VY_MAX) return -STAR_EATER_DASH_VY_MAX;
    return idealVy;
  }
}
